'use client'

import type { Dispatch, SetStateAction } from 'react'
import { createContext, useContext, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { useLocalStorage } from '@helper/web/hooks'

import type { getStickyBanner } from './action'
import { stickyBannerQueryOptions } from './query'

export type StickyBannerData = Awaited<ReturnType<typeof getStickyBanner>>

export interface HasStickyBannerContextValue {
  /** Whether the sticky banner is currently visible (has data and not closed). */
  hasStickyBanner: boolean
  /** The sticky banner data from the query, or null. */
  stickyBanner: StickyBannerData
  /** Whether the user has closed the banner (persisted in localStorage). */
  isClosed: boolean
  /** Set the closed state in localStorage. Call with `true` to dismiss the banner. */
  setClosed: Dispatch<SetStateAction<boolean>>
}

const defaultContextValue: HasStickyBannerContextValue = {
  hasStickyBanner: false,
  stickyBanner: null,
  isClosed: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setClosed: () => {},
}

const HasStickyBannerContext = createContext<HasStickyBannerContextValue>(defaultContextValue)

export interface HasStickyBannerProviderProps {
  children: React.ReactNode
}

/**
 * Provider that exposes whether the sticky banner is currently visible,
 * the sticky banner data, and a setter for the closed state.
 *
 * @example
 * ```tsx
 * // In layout
 * import { HasStickyBannerProvider } from '~/lib/sticky-banner/context'
 *
 * export default function MainLayout({ children }) {
 *   return (
 *     <HasStickyBannerProvider>
 *       <StickyBanner />
 *       <Navbar />
 *       {children}
 *     </HasStickyBannerProvider>
 *   )
 * }
 * ```
 */
export function HasStickyBannerProvider({ children }: HasStickyBannerProviderProps) {
  const { data: stickyBanner } = useQuery(stickyBannerQueryOptions)
  const [isClosed, setClosed] = useLocalStorage('pibo-sticky-banner-marketing-is-closed', false)

  const value = useMemo<HasStickyBannerContextValue>(
    () => ({
      hasStickyBanner: Boolean(stickyBanner && !isClosed),
      stickyBanner: stickyBanner ?? null,
      isClosed,
      setClosed,
    }),
    [stickyBanner, isClosed, setClosed],
  )

  return <HasStickyBannerContext.Provider value={value}>{children}</HasStickyBannerContext.Provider>
}

/**
 * Hook to read sticky banner state and controls.
 * Must be used within a HasStickyBannerProvider.
 */
export function useHasStickyBanner(): HasStickyBannerContextValue {
  return useContext(HasStickyBannerContext)
}
